/* eslint-disable no-unused-vars */
import { LoadingOutlined } from '@ant-design/icons';
import { useMutation } from '@apollo/client';
import { useMatomo } from '@datapunt/matomo-tracker-react';
import { Select, Slider } from 'antd';
import { debounce } from 'lodash';
import isNumber from 'lodash/isNumber';
import nth from 'lodash/nth';
import { useSession } from 'next-auth/react';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import {
  Airplay,
  ArrowsIn,
  ArrowsOut,
  ClosedCaptioning,
  Gauge,
  GearSix,
  Pause,
  PictureInPicture,
  Play,
  Screencast,
  SpeakerHigh,
  SpeakerSlash,
  X
} from 'phosphor-react';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { isChrome, isFirefox, isIOS, isSafari } from 'react-device-detect';
import screenful from 'screenfull';
import {
  actionDimensionIds,
  CONTENT_CATEGORY,
  CONTENT_TYPE,
  defaultSubtitle,
  LIVE_STATUS,
  MATOMO_ACTION,
  MATOMO_CATEGORY,
  MATOMO_NAME,
  PLAYBACK_RATES,
  ROUTES,
  STATIC_DATA_KEYS,
  staticUiLabels,
  SUBSCRIPTION_PLAN_KEYS,
  TRACK_KIND,
  TRACK_MODE,
  TRACK_VALUE,
  VIDEO_PLAYER_NAME
} from '../../../../common/constants';
import useDevice from '../../../../common/useDevice';
import useDeviceInfo from '../../../../common/useDeviceInfo';
import useNetworkType from '../../../../common/useNetworkInfo';
import useStaticData from '../../../../common/useStaticData';
import {
  getUserCommonDimensions,
  Storage,
  withNextVideoQuery
} from '../../../../common/utils';
import { UPDATE_VIDEO_WATCH_TIME } from '../../../../graphql/Mutations';
import { gaEvent } from '../../../../lib/gtag';
import { useApp } from '../../../../providers/AppContext';
import PreviewIndicator from '../../../PreviewIndicator';
import SubscriptionBlock from '../subscriptionModules/SubscriptionBlock';
import {
  LiveEndedState,
  PendingLiveState,
  useLiveVideo
} from '../videoModules/live';
import { LIVE_ENDED_STATE } from '../videoModules/live/LiveEndedState';
import { getLiveVideoLabels } from '../videoModules/live/liveVideoLabels';
import Chapters from './Chapters';
import DurationTime from './DurationTime';
import LiveIndicators from './LiveIndicators';
import LoaderComponent from './LoaderComponent';
import Progress from './Progress';

const ReactPlayer = dynamic(() => import('react-player/lazy'), {
  loading: () => (
    <LoaderComponent setHeight="auto" className="absolute inset-0" />
  ),
  ssr: false
});

const PosterImage = dynamic(() => import('./PosterImage'), {
  loading: () => null,
  ssr: false
});
const IOSWrapper = dynamic(() => import('./IOSWrapper'), {
  loading: () => null,
  ssr: false
});

const { Option } = Select;
let videoWatchTime;

function CustomPlayer(props) {
  const { data: sessionData } = useSession();
  const {
    state: { currentUser, isAuthenticated, accessToken }
  } = useApp();
  const { trackEvent } = useMatomo();
  const router = useRouter();
  const isAuth = !!sessionData;
  const isVideoWatched = useRef(false);
  const [showUnmuteButton, setShowUnmuteButton] = useState(false);
  const maxAllowedWatchCount = useRef(3);
  const [showSignUpModal, setShowSignUpModal] = useState(false);

  const {
    VIDEOS,
    watchTime,
    nextVideo,
    showModal = true,
    videoSlug: previewVideoSlug,
    previewVideo,
    status,
    requiredPlan,
    isPremium,
    tags,
    videoAsset,
    showUserCount = false,
    showGoLiveButton = true
  } = props;
  const videoDuration = 0;
  const playerContainerRef = useRef(null);
  const [airplaySupport, setAirplaySupport] = useState(false);
  const [chromeCastSupport, setChromeCastSupport] = useState(false);
  const [pipSupport, setPipSupport] = useState(true);
  const [activeCaption, setActiveCaption] = useState(null);
  const [captionVisible, setCaptionVisible] = useState(false);
  const [subtitleTracksData, setSubtitleTracksData] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [chapterTitle, setChapterTitle] = useState(null);
  const videoTitle = VIDEOS?.[0]?.title;
  const videoTracks = VIDEOS?.[0]?.tracks;
  const [playbackRate, setPlaybackRate] = useState(1);
  const [playerRef, setPlayerRef] = useState(null);
  const [playing, setPlaying] = useState(true);
  const [duration, setDuration] = useState(videoDuration);
  const [visible, setVisible] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [mobileControls, setMobileControls] = useState(true);
  const [isVideoPlayed, setIsVideoPlayed] = useState(true);
  const [muted, setMuted] = useState(isIOS || isSafari);
  const [volume, setVolume] = useState(1);
  const [pipState, setPipState] = useState(false);
  const [seeking, setSeeking] = useState(false);
  const [seekStartTime, setSeekStartTime] = useState(null);
  const [seekEndTime, setSeekEndTime] = useState(null);
  const [played, setPlayed] = useState(0);
  const [loaded, setLoaded] = useState(0);
  const [playedSeconds, setPlayedSeconds] = useState(0);
  const [playbackOpen, setPlaybackOpen] = useState(false);
  const [settingOpen, setSettingOpen] = useState(false);
  const [subtitleOpen, setSubtitleOpen] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [isBuffering, setIsBuffering] = useState(false);

  const device = useDeviceInfo();
  const networkType = useNetworkType();
  const prevPipState = useRef(pipState);
  const deviceWidth = useDevice();

  const {
    enabled: isVideoALiveVideo,
    isPending,
    isLive,
    isCompleted,
    prevStatus,
    isVodReady,
    isVodProcessing,
    isPaused,
    liveData
  } = useLiveVideo();

  const video =
    isVideoALiveVideo && !isPremium
      ? liveData?.videoUrl || VIDEOS?.[0]?.url
      : VIDEOS?.[0]?.url;

  const uiLabels = useStaticData(`${STATIC_DATA_KEYS.UI_LABELS}/en.json`);
  const {
    PREMIUM_BLOCK_BUTTON,
    PREMIUM_BLOCK_DESC,
    PREMIUM_BLOCK_TITLE,
    PREMIUM_BLOCK_IMAGE,
    WITHOUT_LOGIN_PREMIUM_BLOCK_DESC,
    WITHOUT_LOGIN_PREMIUM_BLOCK_BUTTON,
    WITHOUT_LOGIN_PREMIUM_BLOCK_LOGIN_TEXT,
    WITHOUT_LOGIN_PREMIUM_BLOCK_LOGIN_IMAGE
  } = uiLabels;

  const liveLabels = getLiveVideoLabels(uiLabels);

  const formContentDimension = useCallback(() => {
    const { id, title, url, videoInformation } = VIDEOS?.[0];
    const commaSeparatedTags = tags.map((item) => item.name).join(', ');
    const userDimensions = getUserCommonDimensions(
      currentUser,
      device,
      networkType
    );
    const contentObjectForVideo = [
      { id: actionDimensionIds.content_id, value: id },
      { id: actionDimensionIds.content_title, value: title },
      { id: actionDimensionIds.content_slug, value: url },
      { id: actionDimensionIds.position, value: playedSeconds },
      {
        id: actionDimensionIds.content_duration,
        value: videoInformation?.durationInSeconds
      },
      { id: actionDimensionIds.player, value: VIDEO_PLAYER_NAME },
      { id: actionDimensionIds.sound, value: volume },
      { id: actionDimensionIds.full_screen, value: screenful?.isFullscreen },
      {
        id: actionDimensionIds.quality,
        value: playerRef?.getInternalPlayer('hls')?.levels[
          playerRef?.getInternalPlayer('hls').manualLevel
        ]
          ? `${
              playerRef?.getInternalPlayer('hls')?.levels[
                playerRef?.getInternalPlayer('hls').manualLevel
              ].height
            }p`
          : 'Auto'
      },
      {
        id: actionDimensionIds.bitrate,
        value: playerRef?.getInternalPlayer('hls')?.levels[
          playerRef?.getInternalPlayer('hls').manualLevel
        ]
          ? playerRef?.getInternalPlayer('hls')?.levels[
              playerRef?.getInternalPlayer('hls')?.manualLevel
            ]?.bitrate
          : playerRef?.getInternalPlayer('hls')?.levels[
              playerRef?.getInternalPlayer('hls')?.currentLevel
            ]?.bitrate
      },
      { id: actionDimensionIds.content_category, value: CONTENT_CATEGORY },
      { id: actionDimensionIds.content_type, value: CONTENT_TYPE.VIDEO },
      { id: actionDimensionIds.content_tags, value: commaSeparatedTags },
      { id: actionDimensionIds.content_language, value: defaultSubtitle },
      {
        id: actionDimensionIds.content_closed_caption,
        value: defaultSubtitle
      },
      { id: actionDimensionIds.content_PIP, value: 'internal' }, // content_PIP=internal as it is internal pip feature of react-player
      {
        id: actionDimensionIds.content_preview,
        value: !!previewVideo
      },
      ...userDimensions
    ];
    return contentObjectForVideo;
  }, [
    VIDEOS,
    currentUser,
    device,
    networkType,
    playedSeconds,
    previewVideo,
    playerRef,
    tags,
    volume
  ]);

  useEffect(() => {
    let id;
    if (playbackOpen) {
      return;
    }
    if (showControls && isReady) {
      id = setTimeout(() => {
        setShowControls(false);
      }, 3000);
    }

    return () => {
      if (id) clearTimeout(id);
    };
  }, [showControls, isReady, playbackOpen]);

  useEffect(() => {
    if (isReady && window.location !== window.parent.location) {
      setPlaying(false);
    }
  }, [isReady, playing]);

  const settingClick = () => {
    setSettingOpen(!settingOpen);
    setPlaybackOpen(false);
    setSubtitleOpen(false);
  };

  const playbackClick = () => {
    setPlaybackOpen(!playbackOpen);
    setSettingOpen(false);
    setSubtitleOpen(false);
  };
  const outsideClick = () => {
    setPlaybackOpen(false);
    setSettingOpen(false);
    setSubtitleOpen(false);
  };
  const subtitleClick = () => {
    setSubtitleOpen(!subtitleOpen);
    setPlaybackOpen(false);
    setSettingOpen(false);
  };

  const [updateVideoWatchTime] = useMutation(UPDATE_VIDEO_WATCH_TIME, {
    onError() {}
  });

  useEffect(() => {
    if (isSafari) {
      setAirplaySupport(true);
    }
    if (isFirefox) {
      setPipSupport(false);
    }
    if (isChrome) {
      setChromeCastSupport(true);
    }
  }, []);

  useEffect(() => {
    if (watchTime) {
      if (playerRef) playerRef.seekTo(watchTime ?? 0);
    }
  }, [watchTime, playerRef]);

  useEffect(() => {
    const isFreeOrGuest = [
      SUBSCRIPTION_PLAN_KEYS.FREE,
      SUBSCRIPTION_PLAN_KEYS.GUEST
    ].includes(currentUser?.effectivePlan);

    if (isFreeOrGuest || (isPremium && !previewVideo)) {
      if (isVideoALiveVideo && isFreeOrGuest && !isPremium) {
        return;
      }
      setShowSignUpModal(true);
    }
  }, [currentUser, requiredPlan, previewVideo, isPremium, isVideoALiveVideo]);

  useEffect(() => {
    setIsVideoPlayed(isLive !== true);
  }, [isLive]);

  // useEffect(() => {
  //   if (!isAuth && showModal) {
  //     (async () => {
  //       try {
  //         const resp = await fetch(
  //           `${process?.env?.NEXT_APP_STATIC_DATA_URL}/static/${STATIC_DATA_KEYS.CONFIGS}.json`,
  //           {
  //             method: 'GET',
  //             headers: {
  //               Accept: 'application/json'
  //             }
  //           }
  //         );

  //         const jsonData = await resp.json();
  //         if (jsonData) {
  //           const { VIDEO_SIGNUP_POPUP_INTERVAL } = jsonData ?? {};
  //           maxAllowedWatchCount.current = +(
  //             VIDEO_SIGNUP_POPUP_INTERVAL?.value ?? 3
  //           );
  //         }
  //       } catch (err) {
  //         // eslint-disable-next-line no-console
  //         console.log('ERROR', err);
  //       }
  //     })();
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [isAuth]);

  const updateWatchTime = React.useCallback(
    async (time, track = true) => {
      const id = VIDEOS?.[0]?.id;
      if (!id || !sessionData?.accessToken) return;

      let newWatchTime = time;
      if (Math.floor(time) === Math.floor(duration)) {
        newWatchTime = 0;
      }

      try {
        await updateVideoWatchTime({
          variables: {
            data: {
              videoId: id,
              watchTime: newWatchTime ?? 0
            }
          },
          context: {
            headers: {
              token: sessionData?.accessToken
            }
          }
        });
      } catch (error) {
        return error;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [updateVideoWatchTime, sessionData, playing, duration]
  );

  const debounceUpdateWatchTime = useMemo(
    () => debounce(updateWatchTime, 1000),
    [updateWatchTime]
  );

  useEffect(() => {
    let id;
    if (playing) {
      id = setInterval(() => {
        debounceUpdateWatchTime(videoWatchTime);
      }, 10000);
    }
    return () => {
      debounceUpdateWatchTime(videoWatchTime);
      clearInterval(id);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounceUpdateWatchTime, playing]);

  useEffect(() => {
    if (mobileControls === true) {
      setTimeout(() => {
        if (mobileControls) {
          setMobileControls(false);
        }
      }, 10000);
    }
  }, [mobileControls]);

  const onPause = () => {
    setPlaying(false);
    setPlaybackOpen(false);
    setSettingOpen(false);
    setSubtitleOpen(false);
    updateWatchTime(watchTime);
    trackEvent({
      category: previewVideo
        ? MATOMO_CATEGORY.PREVIEW_VIDEO
        : MATOMO_CATEGORY.VIDEO,
      action: MATOMO_ACTION.CLICK,
      name: MATOMO_NAME.VIDEO_PAUSE,
      customDimensions: formContentDimension(),
      value: TRACK_VALUE.SUCCESS
    });
    gaEvent({
      category: MATOMO_CATEGORY.VIDEO,
      action: MATOMO_ACTION.VIDEO_PAUSE,
      name: JSON.stringify({
        ...(sessionData?.user && {
          userId: sessionData?.user?.id,
          roles: sessionData?.user?.roles?.join(', ')
        }),
        mediaId: VIDEOS?.[0]?.id,
        mediaTitle: VIDEOS?.[0]?.title,
        playerName: VIDEO_PLAYER_NAME,
        mediaType: MATOMO_CATEGORY.VIDEO,
        mediaResource: VIDEOS?.[0]?.resources,
        mediaStatus: status,
        mediaLength: VIDEOS?.[0]?.time,
        mediaProgress: played,
        mediaFullScreen: screenful?.isFullscreen
      }),
      value: TRACK_VALUE.SUCCESS
    });
  };

  const onEnded = () => {
    if (previewVideo) {
      setShowSignUpModal(true);
    }
    trackEvent({
      category: previewVideo
        ? MATOMO_CATEGORY.PREVIEW_VIDEO
        : MATOMO_CATEGORY.VIDEO,
      action: MATOMO_ACTION.CLICK,
      name: MATOMO_NAME.VIDEO_END,
      customDimensions: formContentDimension(),
      value: TRACK_VALUE.SUCCESS
    });
    gaEvent({
      category: MATOMO_CATEGORY.VIDEO,
      action: MATOMO_ACTION.VIDEO_END,
      name: JSON.stringify({
        ...(sessionData?.user && {
          userId: sessionData?.user?.id,
          roles: sessionData?.user?.roles?.join(', ')
        }),
        mediaId: VIDEOS?.[0]?.id,
        mediaTitle: VIDEOS?.[0]?.title,
        playerName: VIDEO_PLAYER_NAME,
        mediaType: MATOMO_CATEGORY.VIDEO,
        mediaResource: VIDEOS?.[0]?.resources,
        mediaStatus: status,
        mediaLength: VIDEOS?.[0]?.time,
        mediaProgress: played,
        mediaFullScreen: screenful?.isFullscreen
      }),
      value: TRACK_VALUE.SUCCESS
    });

    if (nextVideo) {
      const { order, slug, type, videoSlug } = nextVideo;
      const nextVideoPath = withNextVideoQuery(
        `${ROUTES.VIDEOS}/${videoSlug}`,
        slug,
        order,
        type
      );
      router.push(nextVideoPath);
    }
  };

  useEffect(() => {
    if (isReady) {
      // eslint-disable-next-line no-undef
      const playerElement = document?.getElementById('custom-video-player');
      const videoElement = playerElement?.querySelector('video');

      videoElement?.play().catch((err) => {
        if (err?.name === 'NotAllowedError') {
          videoElement.muted = true;
          videoElement.play();
          setShowUnmuteButton(true);
        }
      });
    }
  }, [isReady, isLive]);

  const onReady = (readyRef) => {
    // eslint-disable-next-line no-undef
    const playerElement = document?.getElementById('custom-video-player');
    const videoElement = playerElement?.querySelector('video');
    videoElement.onvolumechange = () => {
      setMuted(videoElement?.muted);
    };
    // for matomo tracking
    videoElement.setAttribute('data-matomo-title', VIDEOS?.[0]?.title);
    videoElement.setAttribute('data-piwik-title', VIDEOS?.[0]?.title);
    videoElement.setAttribute('title', VIDEOS?.[0]?.title);
    videoElement.setAttribute('alt', VIDEOS?.[0]?.title);
    window?.Matomo?.MediaAnalytics?.scanForMedia();
    videoElement?.addEventListener('loadedmetadata', () => {
      const subtitleData = Object?.entries(videoElement?.textTracks)
        ?.map((item) => {
          return nth(item, 1);
        })
        ?.filter((track) => {
          return (
            track?.kind === TRACK_KIND?.SUBTITLES ||
            track?.kind === TRACK_KIND?.CAPTIONS
          );
        });
      setSubtitleTracksData(subtitleData);
    });
    videoElement?.addEventListener('loadedmetadata', () => {
      const subtitleData = Object?.entries(videoElement?.textTracks)
        ?.map((item) => {
          return nth(item, 1);
        })
        ?.filter((track) => {
          return (
            track?.kind === TRACK_KIND?.SUBTITLES ||
            track?.kind === TRACK_KIND?.CAPTIONS
          );
        });
      setSubtitleTracksData(subtitleData);
    });
    const chapterData = Object?.entries(videoElement?.textTracks)
      ?.map((item) => {
        return nth(item, 1);
      })
      ?.filter((track) => {
        return track?.kind === TRACK_KIND?.CHAPTERS;
      });
    setChapters(chapterData);
    setPlayerRef(readyRef);
    setIsReady(true);

    // Auto-seek to live position for live videos
    if (isVideoALiveVideo && isLive) {
      // Small delay to ensure video is fully loaded
      setTimeout(() => {
        if (videoElement?.duration) {
          readyRef?.seekTo(videoElement.duration);
        }
      }, 350);
    }
  };

  const onStart = () => {
    trackEvent({
      category: previewVideo
        ? MATOMO_CATEGORY.PREVIEW_VIDEO
        : MATOMO_CATEGORY.VIDEO,
      action: MATOMO_ACTION.CLICK,
      name: MATOMO_NAME.VIDEO_START,
      customDimensions: formContentDimension(),
      value: TRACK_VALUE.SUCCESS
    });
    trackEvent({
      category: previewVideo
        ? MATOMO_CATEGORY.PREVIEW_VIDEO
        : MATOMO_CATEGORY.VIDEO,
      action: MATOMO_ACTION.CLICK,
      name: MATOMO_NAME.VIDEO_PLAYING,
      customDimensions: formContentDimension(),
      value: TRACK_VALUE.SUCCESS
    });
  };

  const onError = () => {
    trackEvent({
      category: MATOMO_CATEGORY.VIDEO,
      action: MATOMO_ACTION.CLICK,
      name: MATOMO_NAME.VIDEO_ERROR,
      customDimensions: formContentDimension(),
      value: TRACK_VALUE.SUCCESS
    });
  };

  const onBuffer = () => {
    setIsBuffering(true);
  };

  const onBufferEnd = () => {
    setIsBuffering(false);
  };

  const onDuration = (movieDuration) => {
    setDuration(movieDuration);
  };

  const handleDurationChange = (event) => {
    if (!isVideoALiveVideo) return;
    const { duration: newDuration } = event?.target || {};
    setDuration(newDuration || 0);
  };

  const onProgress = React.useCallback(
    // eslint-disable-next-line no-shadow
    ({ played, loaded, playedSeconds }) => {
      // We only want to update time slider if we are not currently seeking
      if (!seeking) {
        setPlayed(played);
        setLoaded(loaded);
        setPlayedSeconds(playedSeconds);
        videoWatchTime = playedSeconds;
        const chapter =
          nth(chapters, 0)?.cues?.length > 0 &&
          Object?.entries(nth(chapters, 0)?.cues)?.find(
            (item) =>
              playedSeconds?.toFixed(0) >= nth(item, 1)?.startTime &&
              playedSeconds?.toFixed(0) < nth(item, 1)?.endTime
          );
        setChapterTitle(nth(chapter, 1)?.text);
      }
    },
    [seeking, chapters]
  );
  const handleMouseDown = () => {
    setSeeking(true);
    setSeekStartTime(played);
    trackEvent({
      category: MATOMO_CATEGORY.VIDEO,
      action: MATOMO_ACTION.CLICK,
      name: MATOMO_NAME.VIDEO_SEEK_STARTED,
      customDimensions: [
        ...formContentDimension(),
        { id: actionDimensionIds.seek_position, value: played * duration }
      ],
      value: TRACK_VALUE.SUCCESS
    });
  };

  const handleMouseUp = () => {
    setSeeking(false);
    setSeekEndTime(played);
    trackEvent({
      category: MATOMO_CATEGORY.VIDEO,
      action: MATOMO_ACTION.CLICK,
      name: MATOMO_NAME.VIDEO_SEEK_COMPLETED,
      customDimensions: [
        ...formContentDimension(),
        { id: actionDimensionIds.seek_position, value: played * duration }
      ],
      value: TRACK_VALUE.SUCCESS
    });
  };
  const onSeekChange = React.useCallback(
    async (event) => {
      const time = parseFloat(event?.currentTarget?.value);
      setPlayed(time);
      playerRef?.seekTo(time);
      setPlaybackOpen(false);
      setSettingOpen(false);
      setSubtitleOpen(false);
    },
    [playerRef]
  );

  const handleBack15 = React.useCallback(async () => {
    try {
      const back15 = duration * played - 15;
      const time = back15 <= 0 ? 0 : back15 / duration;
      setPlaybackOpen(false);
      setSettingOpen(false);
      setSubtitleOpen(false);
      return playerRef?.seekTo(time);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('handleBack error =>', err);
    }
  }, [duration, played, playerRef]);

  const handleForward15 = React.useCallback(async () => {
    try {
      const forward15 = duration * played + 15;
      if (forward15 >= duration) return false;
      const time = forward15 / duration;
      setPlaybackOpen(false);
      setSettingOpen(false);
      setSubtitleOpen(false);
      return playerRef?.seekTo(time);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('handleForward error =>', err);
    }
  }, [duration, played, playerRef]);

  const toggleFullScreen = () => {
    screenful.toggle(playerContainerRef?.current);
    if (!screenful.isFullscreen) {
      trackEvent({
        category: MATOMO_CATEGORY.VIDEO,
        action: MATOMO_ACTION.CLICK,
        name: MATOMO_NAME.VIDEO_FULL_SCREEN,
        customDimensions: formContentDimension(),
        value: TRACK_VALUE.SUCCESS
      });
    }
  };

  useEffect(() => {
    const handleFullScreenChange = () => {
      setIsFullScreen(screenful.isFullscreen);

      if (!screenful.isFullscreen) {
        trackEvent({
          category: MATOMO_CATEGORY.VIDEO,
          action: MATOMO_ACTION.CLICK,
          name: MATOMO_NAME.VIDEO_EXIT_FULL_SCREEN,
          customDimensions: formContentDimension(),
          value: TRACK_VALUE.SUCCESS
        });
      }
    };

    if (screenful.isEnabled) {
      screenful.on('change', handleFullScreenChange);
    }

    return () => {
      if (screenful.isEnabled) {
        screenful.off('change', handleFullScreenChange);
      }
    };
  }, [formContentDimension, trackEvent]);

  const handleVolumeChange = (newValue) => {
    setVolume(parseFloat(newValue / 100));
    setMuted(newValue === 0);
    setPlaybackOpen(false);
    setSettingOpen(false);
    setSubtitleOpen(false);
  };

  const hanldeMute = () => {
    if (muted) {
      trackEvent({
        category: MATOMO_CATEGORY.VIDEO,
        action: MATOMO_ACTION.CLICK,
        name: MATOMO_NAME.VIDEO_UNMUTE,
        customDimensions: formContentDimension(),
        value: TRACK_VALUE.SUCCESS
      });
    } else {
      trackEvent({
        category: MATOMO_CATEGORY.VIDEO,
        action: MATOMO_ACTION.CLICK,
        name: MATOMO_NAME.VIDEO_MUTE,
        customDimensions: formContentDimension(),
        value: TRACK_VALUE.SUCCESS
      });
    }
    setMuted(!muted);
    setPlaybackOpen(false);
    setSettingOpen(false);
    setSubtitleOpen(false);
  };

  const onCaptionClick = (event, track) => {
    event?.preventDefault();
    setCaptionVisible(true);
    // eslint-disable-next-line no-undef
    const playerElement = document?.getElementById('custom-video-player');
    const videoElement = playerElement?.querySelector('video');
    if (videoElement?.textTracks?.length > 0) {
      const showingTracks = Object?.entries(videoElement?.textTracks)?.find(
        (item) => {
          return (
            (nth(item, 1)?.kind === TRACK_KIND?.SUBTITLES ||
              nth(item, 1)?.kind === TRACK_KIND?.CAPTIONS) &&
            nth(item, 1)?.mode === TRACK_MODE?.SHOWING
          );
        }
      );
      if (showingTracks?.length > 0) {
        videoElement.textTracks[nth(showingTracks, 0)].mode =
          TRACK_MODE?.DISABLED;
      }
      const tracks = Object.entries(videoElement?.textTracks)?.find((item) => {
        return (
          (nth(item, 1)?.kind === TRACK_KIND?.SUBTITLES ||
            nth(item, 1)?.kind === TRACK_KIND?.CAPTIONS) &&
          nth(item, 1)?.label === track?.label
        );
      });
      videoElement.textTracks[nth(tracks, 0)].mode = TRACK_MODE?.SHOWING;
    }
    // TODO - Hide caption menu once any button is selected
  };
  const onChangeSubTitle = () => {
    setSubtitleOpen(false);
  };
  const onChangeBitrate = (value) => {
    setSettingOpen(false);
    const internalPlayer = playerRef?.getInternalPlayer('hls');
    if (internalPlayer) {
      // currentLevel expect to receive an index of the levels array
      internalPlayer.currentLevel = value;
      setPlayerRef({ ...playerRef, ...internalPlayer });
    }
  };

  const onPlaybackRate = (rate) => {
    setPlaybackRate(rate);
    setPlaybackOpen(!playbackOpen);
  };

  const handleCasting = () => {
    trackEvent({
      category: MATOMO_CATEGORY.VIDEO,
      action: MATOMO_ACTION.CLICK,
      name: MATOMO_NAME.VIDEO_CAST,
      customDimensions: [
        ...formContentDimension(),
        {
          id: actionDimensionIds.cast_type,
          value: isSafari ? 'airplay' : 'chromecast'
        }
      ]
    });
    // eslint-disable-next-line no-undef
    const cjs = new Castjs();
    if (cjs?.available) {
      // Initiate new cast session with a simple video
      cjs?.cast(video, {
        title: videoTitle,
        subtitles: videoTracks
      });
    }
  };

  const pipHandler = () => {
    setPipState(!pipState);
  };

  const handleAirplay = () => {
    // eslint-disable-next-line no-undef
    const playerElement = document?.getElementById('custom-video-player');
    const videoElement = playerElement?.querySelector('video');
    videoElement?.webkitShowPlaybackTargetPicker();
  };

  const handleShowCaptions = () => {
    setCaptionVisible(!captionVisible);
    // eslint-disable-next-line no-undef
    const playerElement = document?.getElementById('custom-video-player');
    const videoElement = playerElement?.querySelector('video');
    if (videoElement?.textTracks?.length > 0) {
      const showingTracks = Object?.entries(videoElement?.textTracks)?.find(
        (item) => {
          return (
            (nth(item, 1)?.kind === TRACK_KIND?.SUBTITLES ||
              nth(item, 1)?.kind === TRACK_KIND?.CAPTIONS) &&
            nth(item, 1)?.mode === TRACK_MODE?.SHOWING
          );
        }
      );
      if (showingTracks?.length > 0) {
        videoElement.textTracks[nth(showingTracks, 0)].mode =
          TRACK_MODE?.DISABLED;
        setActiveCaption(videoElement.textTracks[nth(showingTracks, 0)].label);
      } else {
        const disabledTracks = Object?.entries(videoElement?.textTracks)?.find(
          (item) => {
            return (
              nth(item, 1)?.kind === TRACK_KIND?.SUBTITLES ||
              (nth(item, 1)?.kind === TRACK_KIND?.CAPTIONS &&
                nth(item, 1)?.label === activeCaption)
            );
          }
        );
        if (disabledTracks?.length > 0) {
          videoElement.textTracks[nth(disabledTracks, 0)].mode =
            TRACK_MODE?.SHOWING;
        }
      }
    }
  };

  const subtitleContent = () => {
    return (
      <div className="video-dropdown">
        <div className="video-setting-icon" onClick={subtitleClick}>
          <ClosedCaptioning
            className={captionVisible ? 'active-caption pointer' : 'pointer'}
            size={32}
            onClick={handleShowCaptions}
          />
        </div>

        <Select
          bordered={false}
          defaultValue={defaultSubtitle}
          placement="bottomRight"
          onSelect={onChangeSubTitle}
          onChange={onChangeSubTitle}
          open={subtitleOpen}
          getPopupContainer={(trigger) => trigger?.parentNode}
          // eslint-disable-next-line react/no-unstable-nested-components
          dropdownRender={(menu) => (
            <>
              <div className="text-meta-bold dropdown-header">
                Subtitles
                <X color="#1A1714" size={24} onClick={subtitleClick} />
              </div>
              {menu}
            </>
          )}
        >
          {subtitleTracksData?.map((track) => {
            return (
              <Option key={track?.language} value={track?.language}>
                <span
                  onClick={(event) => {
                    onCaptionClick(event, track);
                  }}
                >
                  {track?.label}
                </span>
              </Option>
            );
          })}
        </Select>
      </div>
    );
  };

  const qualityContent = () => {
    return (
      <div className="video-dropdown">
        <div className="video-setting-icon" onClick={settingClick}>
          <GearSix className="pointer" size={32} />
        </div>
        <Select
          bordered={false}
          value={playerRef?.getInternalPlayer('hls')?.manualLevel}
          onChange={(e) => {
            onChangeBitrate(e);
            trackEvent({
              category: previewVideo
                ? MATOMO_CATEGORY.PREVIEW_VIDEO
                : MATOMO_CATEGORY.VIDEO,
              action: MATOMO_ACTION.CLICK,
              name: MATOMO_NAME.VIDEO_QUALITY_UPDATED,
              customDimensions: formContentDimension(),
              value: TRACK_VALUE.SUCCESS
            });
          }}
          onSelect={onChangeBitrate}
          open={settingOpen}
          placement="bottomRight"
          getPopupContainer={(trigger) => trigger?.parentNode}
          // eslint-disable-next-line react/no-unstable-nested-components
          dropdownRender={(menu) => (
            <>
              <div className="text-meta-bold dropdown-header">
                Quality
                <X color="#1A1714" size={24} onClick={settingClick} />
              </div>
              {menu}
            </>
          )}
        >
          <Option key="auto" value={-1}>
            Auto
          </Option>

          {playerRef?.getInternalPlayer('hls')?.levels?.map((level, id) => {
            return (
              // eslint-disable-next-line react/no-array-index-key
              <Option key={id} value={id}>
                {level?.height}p
              </Option>
            );
          })}
        </Select>
      </div>
    );
  };

  const playbackRateContent = () => {
    return (
      <div className="video-dropdown">
        <div className="video-setting-icon" onClick={playbackClick}>
          <Gauge size={32} />
        </div>
        <Select
          bordered={false}
          className="playback-rate-selector"
          defaultValue={playbackRate}
          open={playbackOpen}
          placement="topRight"
          onChange={onPlaybackRate}
          onSelect={onPlaybackRate}
          getPopupContainer={(trigger) => trigger?.parentNode}
          // eslint-disable-next-line react/no-unstable-nested-components
          dropdownRender={(menu) => (
            <>
              <div className="text-meta-bold dropdown-header">
                Playback speed
                <X color="#1A1714" size={24} onClick={playbackClick} />
              </div>
              {menu}
            </>
          )}
        >
          {PLAYBACK_RATES?.map((rate) => {
            return (
              <Option key={rate} value={rate}>
                {rate}
              </Option>
            );
          })}
        </Select>
      </div>
    );
  };

  const handleCancel = () => {
    setVisible(false);
  };

  const handleMouseOver = () => {
    setShowControls(true);
  };

  const handleMouseOut = () => {
    setShowControls(false);
    setSettingOpen(false);
    setPlaybackOpen(false);
    setSubtitleOpen(false);
  };

  const handleChapter = (item) => {
    playerRef?.seekTo(item?.startTime);
  };

  const seekToLive = () => {
    // eslint-disable-next-line no-undef
    const playerElement = document?.getElementById('custom-video-player');
    const internalPlayer = playerElement?.querySelector('video');
    if (internalPlayer?.duration) {
      playerRef?.seekTo(internalPlayer.duration);
    }
    setPlaying(true);
  };

  const onPlay = () => {
    const internalPlayer = document?.querySelector('video');

    if (
      internalPlayer &&
      !isAuth &&
      !isVideoWatched.current &&
      showModal &&
      window.location === window.parent.location
    ) {
      const count = +Storage.get(Storage.keys.VIDEO_WATCH_COUNT);

      // if (count >= maxAllowedWatchCount.current ?? 3) {
      //   setShowSignUpModal(true);
      //   setMuted(false);
      //   internalPlayer?.pause();
      //   return;
      // }
      const videoWatchCount = (isNumber(count) ? count : 0) + 1;
      Storage.set({
        key: Storage.keys.VIDEO_WATCH_COUNT,
        value: videoWatchCount
      });
      isVideoWatched.current = true;
    }
    if (played > 0) {
      trackEvent({
        category: previewVideo
          ? MATOMO_CATEGORY.PREVIEW_VIDEO
          : MATOMO_CATEGORY.VIDEO,
        action: MATOMO_ACTION.CLICK,
        name: MATOMO_NAME.VIDEO_PLAY,
        customDimensions: formContentDimension(),
        value: TRACK_VALUE.SUCCESS
      });
      trackEvent({
        category: previewVideo
          ? MATOMO_CATEGORY.PREVIEW_VIDEO
          : MATOMO_CATEGORY.VIDEO,
        action: MATOMO_ACTION.CLICK,
        name: MATOMO_NAME.VIDEO_PLAYING,
        customDimensions: formContentDimension(),
        value: TRACK_VALUE.SUCCESS
      });
    }
    gaEvent({
      category: MATOMO_CATEGORY.VIDEO,
      action: MATOMO_ACTION.VIDEO_PLAY,
      name: JSON.stringify({
        ...(sessionData?.user && {
          userId: sessionData?.user?.id,
          roles: sessionData?.user?.roles?.join(', ')
        }),
        mediaId: VIDEOS?.[0]?.id,
        mediaTitle: VIDEOS?.[0]?.title,
        playerName: VIDEO_PLAYER_NAME,
        mediaType: MATOMO_CATEGORY.VIDEO,
        mediaResource: VIDEOS?.[0]?.resources,
        mediaStatus: status,
        mediaLength: VIDEOS?.[0]?.time,
        mediaProgress: played,
        mediaFullScreen: screenful?.isFullscreen
      }),
      value: TRACK_VALUE.SUCCESS
    });

    setPlaying(true);
    setIsVideoPlayed(false);
    setPlaybackOpen(false);
    setSettingOpen(false);
    setSubtitleOpen(false);
  };

  const handlePlayPause = () => {
    setPlaying(!playing);
  };

  const handleSignUpModalClose = () => {
    setShowSignUpModal(false);
  };

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleRouteChange = () => {
      trackEvent({
        category: previewVideo
          ? MATOMO_CATEGORY.PREVIEW_VIDEO
          : MATOMO_CATEGORY.VIDEO,
        action: MATOMO_ACTION.CLICK,
        name: MATOMO_NAME.VIDEO_CLOSE,
        customDimensions: formContentDimension(),
        value: TRACK_VALUE.SUCCESS
      });
    };

    // Listen for route changes
    router.events.on('routeChangeStart', handleRouteChange);

    // Cleanup on unmount
    return () => {
      router.events.off('routeChangeStart', handleRouteChange);
      window.removeEventListener('popstate', handleRouteChange);
    };
  }, [playing, router.events, trackEvent, formContentDimension, previewVideo]);

  useEffect(() => {
    if (pipState !== prevPipState.current) {
      trackEvent({
        category: MATOMO_CATEGORY.VIDEO,
        action: MATOMO_ACTION.CLICK,
        name: pipState ? MATOMO_NAME.VIDEO_PIP_IN : MATOMO_NAME.VIDEO_PIP_EXIT,
        customDimensions: formContentDimension(),
        value: TRACK_VALUE.SUCCESS
      });
      prevPipState.current = pipState;
    }
  }, [pipState, formContentDimension, trackEvent]);

  const getGAPayloadForLiveVideo = (action) => {
    const fullUrlWithoutParams =
      typeof window !== 'undefined'
        ? `${window.location.origin}${window.location.pathname}`
        : '';

    return JSON.stringify({
      item_id: VIDEOS?.[0]?.id,
      link_url: fullUrlWithoutParams,
      link_text: VIDEOS?.[0]?.title,
      action
    });
  };

  const [isWatchAgainClicked, setIsWatchAgainClicked] = useState(false);

  const actualLiveVideoUrl = liveData?.videoUrl;

  const isPreviewOnly = !!previewVideo && !actualLiveVideoUrl;

  // Screen State Inputs
  const isPreLiveActive = !isPremium && isPending;

  const isReplayComingSoon = (() => {
    const isActualVodProcessing = !isPremium && isVodProcessing;
    if (prevStatus === LIVE_STATUS.VOD_PROCESSING) {
      return isActualVodProcessing;
    }
    if (isWatchAgainClicked) {
      return true;
    }
    return isActualVodProcessing;
  })();

  const isStreamEnded = !isPremium && isCompleted && !isWatchAgainClicked;
  const isJustAPostLiveVod = !isPremium && isVodReady;
  const isLiveStreamPaused = !isPremium && isPaused;

  const isVideoNotAccessible = isVideoALiveVideo && isPremium && !video;

  const getIsReactPlayerDisabled = () => {
    if (!isVideoALiveVideo) return false;

    if (
      isPreLiveActive ||
      isLiveStreamPaused ||
      isStreamEnded ||
      isReplayComingSoon ||
      isVideoNotAccessible
    )
      return true;

    if (isJustAPostLiveVod || isLive) return false;

    return false;
  };

  // Player Enabled State
  const isReactPlayerDisabled = getIsReactPlayerDisabled();
  const isReactPlayerEnabled = !isReactPlayerDisabled;

  const isLiveIndicatorsEnabled = !isPremium && isReactPlayerEnabled && isLive;

  const LIVE_THRESHOLD_SECONDS = 30;
  const HALF_MINUTE = 30;
  const ONE_MINUTE = 60;

  const liveProgressLabel = uiLabels?.VIDEO_LIVE_PROGRESS_LABEL?.value;

  // Determine if user is behind live stream using UI labels logic
  const isBehind = useMemo(() => {
    if (!isVideoALiveVideo || !isLive || !duration) return false;

    // Get behind threshold from UI labels
    const behindNumber = parseInt(
      liveProgressLabel || LIVE_THRESHOLD_SECONDS.toString(),
      10
    );

    const progress = playedSeconds;
    const playerDuration = duration;

    return (
      progress &&
      progress >= HALF_MINUTE && // user has watched at least 30 sec
      playerDuration >= ONE_MINUTE && // stream is at least 1 min long
      playerDuration - progress >= behindNumber // user is behind live by >= threshold
    );
  }, [isVideoALiveVideo, isLive, playedSeconds, duration, liveProgressLabel]);

  const getGoLiveButtonState = useMemo(() => {
    if (!isVideoALiveVideo) return null;
    if (!playing) return 'paused';
    if (isBehind) return 'paused'; // Behind the live threshold

    return 'live';
  }, [isVideoALiveVideo, isBehind, playing]);

  const liveBadgeState = useMemo(() => {
    if (!isVideoALiveVideo) return null;
    if (isBehind) return 'paused'; // Behind the live threshold
    return 'live';
  }, [isVideoALiveVideo, isBehind]);

  // Track pre-live player visits
  useEffect(() => {
    if (isVideoALiveVideo && isPreLiveActive) {
      trackEvent({
        category: MATOMO_CATEGORY.VIDEO,
        action: MATOMO_ACTION.VISIT_PRE_LIVE_PLAYER,
        name: MATOMO_NAME.VISIT_PRE_LIVE_PLAYER,
        customDimensions: formContentDimension(),
        value: TRACK_VALUE.SUCCESS
      });
      gaEvent({
        category: MATOMO_CATEGORY.VIDEO,
        action: MATOMO_NAME.VISIT_PRE_LIVE_PLAYER,
        name: getGAPayloadForLiveVideo(MATOMO_ACTION.VIEW),
        value: TRACK_VALUE.SUCCESS
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVideoALiveVideo, isPreLiveActive]);

  // Track replay player visits
  useEffect(() => {
    if (isVideoALiveVideo && isReplayComingSoon) {
      trackEvent({
        category: MATOMO_CATEGORY.VIDEO,
        action: MATOMO_ACTION.VISIT_REPLAY_PLAYER,
        name: MATOMO_NAME.VISIT_REPLAY_PLAYER,
        customDimensions: formContentDimension(),
        value: TRACK_VALUE.SUCCESS
      });
      gaEvent({
        category: MATOMO_CATEGORY.VIDEO,
        action: MATOMO_NAME.VISIT_REPLAY_PLAYER,
        name: getGAPayloadForLiveVideo(MATOMO_ACTION.VIEW),
        value: TRACK_VALUE.SUCCESS
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVideoALiveVideo, isReplayComingSoon]);

  // Live Video Controls Logic
  const isLiveVideoPlayable = (() => {
    if (isReactPlayerEnabled && isVideoALiveVideo) {
      if (isPremium && previewVideo) {
        return false;
      }

      if (isPreviewOnly) {
        return false;
      }

      if (isJustAPostLiveVod) {
        return false;
      }

      return true;
    }

    return false;
  })();

  const getLiveEndedState = () => {
    if (isStreamEnded) {
      return LIVE_ENDED_STATE.VIDEO_ENDED;
    }
    if (isLiveStreamPaused) {
      return LIVE_ENDED_STATE.LIVE_PAUSED;
    }
    if (isReplayComingSoon) {
      return LIVE_ENDED_STATE.POST_LIVE_PENDING;
    }

    return null;
  };

  return (
    <>
      {showSignUpModal && (
        <SubscriptionBlock
          primaryAction
          thumb={
            isAuthenticated
              ? PREMIUM_BLOCK_IMAGE?.value ||
                staticUiLabels?.PREMIUM_BLOCK_IMAGE
              : WITHOUT_LOGIN_PREMIUM_BLOCK_LOGIN_IMAGE?.value ||
                staticUiLabels?.WITHOUT_LOGIN_PREMIUM_BLOCK_LOGIN_IMAGE
          }
          title={
            isAuthenticated
              ? PREMIUM_BLOCK_TITLE?.value || staticUiLabels.PREMIUM_BLOCK_TITLE
              : staticUiLabels?.WITHOUT_LOGIN_PREMIUM_BLOCK_TITLE
          }
          description={
            isAuthenticated
              ? PREMIUM_BLOCK_DESC?.value || staticUiLabels.PREMIUM_BLOCK_DESC
              : WITHOUT_LOGIN_PREMIUM_BLOCK_DESC?.value ||
                staticUiLabels?.WITHOUT_LOGIN_PREMIUM_BLOCK_DESC
          }
          btnLabel={
            isAuthenticated
              ? PREMIUM_BLOCK_BUTTON?.value ||
                staticUiLabels.PREMIUM_BLOCK_BUTTON
              : WITHOUT_LOGIN_PREMIUM_BLOCK_BUTTON?.value ||
                staticUiLabels?.WITHOUT_LOGIN_PREMIUM_BLOCK_BUTTON
          }
          loginText={
            !isAuthenticated && WITHOUT_LOGIN_PREMIUM_BLOCK_LOGIN_TEXT?.value
          }
          modal={showSignUpModal}
          videoSlug={previewVideoSlug}
          setShowSubscribeModal={setShowSignUpModal}
          previewVideo={previewVideo}
          isPremium={isPremium}
          requiredPlan={requiredPlan}
        />
      )}
      <div
        className={`player-wrapper ${mobileControls && 'mobile-controls'}`}
        ref={playerContainerRef}
        onMouseMove={handleMouseOver}
        onTouchStart={handleMouseOver}
        onMouseLeave={handleMouseOut}
      >
        {isPreLiveActive && <PendingLiveState uiLabels={uiLabels} />}
        {getLiveEndedState() && (
          <LiveEndedState
            state={getLiveEndedState()}
            onWatchAgain={() => {
              // Track the watch replay button click
              trackEvent({
                category: MATOMO_CATEGORY.VIDEO,
                action: MATOMO_ACTION.CLICK_WATCH_REPLAY_BUTTON,
                name: MATOMO_NAME.CLICK_WATCH_REPLAY_BUTTON,
                customDimensions: [
                  {
                    id: actionDimensionIds.page_source,
                    value: router.asPath
                  },
                  {
                    id: actionDimensionIds.content_id,
                    value: VIDEOS?.[0]?.id
                  },
                  {
                    id: actionDimensionIds.content_title,
                    value: VIDEOS?.[0]?.title
                  },
                  {
                    id: actionDimensionIds.content_slug,
                    value: VIDEOS?.[0]?.slug
                  },
                  {
                    id: actionDimensionIds.cta_title,
                    value: getLiveVideoLabels(uiLabels).WATCH_REPLAY_BUTTON
                  }
                ],
                value: TRACK_VALUE.SUCCESS
              });
              gaEvent({
                category: MATOMO_CATEGORY.VIDEO,
                action: MATOMO_NAME.CLICK_WATCH_REPLAY_BUTTON,
                name: getGAPayloadForLiveVideo(MATOMO_ACTION.CLICK),
                value: TRACK_VALUE.SUCCESS
              });

              setIsWatchAgainClicked(true);
            }}
            uiLabels={uiLabels}
          />
        )}

        {(isReactPlayerEnabled || isVideoNotAccessible) && (
          <PosterImage
            url={VIDEOS?.[0]?.thumb}
            isIos={isIOS}
            isVideoPlayed={isVideoNotAccessible ? true : isVideoPlayed}
          />
        )}

        {isReactPlayerEnabled && (
          <>
            {showUnmuteButton && (
              <div
                className="tap-to-unmute"
                onClick={() => {
                  setMuted(false);
                  setShowUnmuteButton(false);
                }}
              >
                <SpeakerSlash size={24} />
                Tap To Unmute
              </div>
            )}

            <ReactPlayer
              className="custom-player"
              url={video}
              key={video}
              data-matomo-title={VIDEOS?.[0]?.title}
              width="100%"
              height="100%"
              id="custom-video-player"
              playsinline
              controls={isIOS}
              loop={false}
              volume={volume}
              muted={muted}
              playing={playing}
              playbackRate={playbackRate}
              onReady={onReady}
              onPause={onPause}
              onPlay={onPlay}
              onDuration={onDuration}
              onDurationChange={handleDurationChange}
              onProgress={onProgress}
              onEnded={onEnded}
              onStart={onStart}
              onSeek={debounceUpdateWatchTime}
              onError={onError}
              onBuffer={onBuffer}
              onBufferEnd={onBufferEnd}
              pip={pipState}
              onDisablePIP={() => setPipState(false)}
              config={{
                file: {
                  attributes: {
                    crossOrigin: 'true'
                  },
                  tracks: videoTracks
                }
              }}
            />

            <IOSWrapper show={!isIOS}>
              {showControls && isReady && (
                <div className="custom-control-wrap">
                  {(playbackOpen || settingOpen || subtitleOpen) && (
                    <span className="overlay-outside" onClick={outsideClick} />
                  )}

                  <div className="controlitem-top d-flex justify-between ">
                    <div className="text-md text-white-90">
                      {typeof document !== 'undefined' &&
                        (document?.fullscreenElement ||
                          document?.webkitCurrentFullScreenElement) &&
                        videoTitle}
                    </div>
                    <div className="d-flex text-n-0 gap-16">
                      {!isLiveVideoPlayable &&
                        !previewVideo &&
                        !!subtitleTracksData?.length &&
                        subtitleContent()}

                      {!isLiveVideoPlayable && qualityContent()}
                      {/* Live Indicators */}
                      {isLiveIndicatorsEnabled && (
                        <div>
                          <LiveIndicators
                            viewerCount={liveData?.liveInfo?.viewerCount || 0}
                            uiLabels={uiLabels}
                            showUserCount={showUserCount}
                            state={liveBadgeState}
                            onSeekToLive={seekToLive}
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="controlitem-middle">
                    <div className="play-pause-btn">
                      {/* Hide seek controls for live videos */}
                      {!isLiveVideoPlayable && (
                        <svg
                          onClick={handleBack15}
                          cursor="pointer"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M9 10.4942L10.5294 9.47461V15.4111"
                            stroke="#fff"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M16.2907 9.47461H13.732L13.3496 12.43C13.6863 12.0957 14.1416 11.9081 14.6162 11.9081C15.0909 11.9081 15.5462 12.0957 15.8829 12.43C16.0494 12.5952 16.1815 12.7917 16.2717 13.0082C16.3618 13.2247 16.4083 13.4569 16.4083 13.6913C16.4083 13.9258 16.3618 14.158 16.2717 14.3745C16.1815 14.5909 16.0494 14.7874 15.8829 14.9526C15.5462 15.287 15.0909 15.4746 14.6162 15.4746C14.1416 15.4746 13.6863 15.287 13.3496 14.9526"
                            stroke="#fff"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M6.38867 8.93639H2.05979V4.60645"
                            stroke="#fff"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M5.73955 18.7336C7.07113 20.0666 8.76805 20.9746 10.6156 21.3428C12.4632 21.711 14.3785 21.5228 16.1192 20.802C17.8598 20.0812 19.3477 18.8603 20.3945 17.2936C21.4413 15.7269 22 13.8849 22 12.0005C22 10.1161 21.4413 8.27408 20.3945 6.70739C19.3477 5.1407 17.8598 3.91975 16.1192 3.19898C14.3785 2.47822 12.4632 2.29003 10.6156 2.65822C8.76805 3.02641 7.07113 3.93443 5.73955 5.26743L2.06 8.93706"
                            stroke="#fff"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}

                      <div
                        className="play-pause-btn pointer"
                        onClick={handlePlayPause}
                      >
                        {isBuffering && <LoadingOutlined spin />}
                        {!isBuffering && playing && (
                          <Pause size={80} weight="fill" />
                        )}
                        {!isBuffering && !playing && (
                          <Play size={80} weight="fill" />
                        )}
                      </div>

                      {/* Hide seek controls for live videos */}
                      {!isLiveVideoPlayable && (
                        <svg
                          onClick={handleForward15}
                          cursor="pointer"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M18.0254 8.93639H22.3553V4.60645"
                            stroke="#fff"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M18.6746 18.7336C17.3427 20.0666 15.6454 20.9746 13.7973 21.3428C11.9492 21.711 10.0335 21.5228 8.29243 20.802C6.55135 20.0812 5.06316 18.8603 4.0161 17.2936C2.96904 15.7269 2.41016 13.8849 2.41016 12.0005C2.41016 10.1161 2.96904 8.27408 4.0161 6.70739C5.06316 5.1407 6.55135 3.91975 8.29243 3.19898C10.0335 2.47822 11.9492 2.29003 13.7973 2.65822C15.6454 3.02641 17.3427 3.93443 18.6746 5.26743L22.355 8.93706"
                            stroke="#fff"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M7.72656 10.4942L9.256 9.47461V15.4111"
                            stroke="#fff"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M15.0193 9.47463H12.4605L12.0781 12.4301C12.4148 12.0957 12.8702 11.9081 13.3448 11.9081C13.8194 11.9081 14.2747 12.0957 14.6114 12.4301C14.7779 12.5953 14.91 12.7918 15.0002 13.0082C15.0904 13.2247 15.1368 13.4569 15.1368 13.6913C15.1368 13.9258 15.0904 14.158 15.0002 14.3745C14.91 14.5909 14.7779 14.7874 14.6114 14.9526C14.2747 15.287 13.8194 15.4746 13.3448 15.4746C12.8702 15.4746 12.4148 15.287 12.0781 14.9526"
                            stroke="#fff"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </div>
                  </div>
                  <div className="controlitem-bottom">
                    {/* Go Live Button - Only for live videos */}
                    {showGoLiveButton &&
                      isLiveVideoPlayable &&
                      isLiveIndicatorsEnabled && (
                        <div className="go-live-button-container">
                          <button
                            onClick={seekToLive}
                            className="go-live-button"
                            type="button"
                          >
                            <span
                              className={`status-dot ${getGoLiveButtonState}`}
                            />
                            {liveLabels.GO_LIVE_BUTTON}
                          </button>
                        </div>
                      )}
                    <div className="duration-bar">
                      {isLiveVideoPlayable ? null : (
                        <Progress
                          loaded={loaded}
                          played={played}
                          onChange={onSeekChange}
                          handleMouseDown={handleMouseDown}
                          handleMouseUp={handleMouseUp}
                        />
                      )}
                    </div>
                    <div className="controls-items">
                      <div className="left-controls">
                        <div className="play-box">
                          {isLiveVideoPlayable ? null : (
                            /* Regular video time display */
                            <div className="play-duration">
                              <DurationTime seconds={duration * played} />/
                              <DurationTime seconds={duration} />
                            </div>
                          )}
                        </div>
                        <div className="volume-section ">
                          {muted ? (
                            <SpeakerSlash
                              size={32}
                              onClick={hanldeMute}
                              className="icon"
                            />
                          ) : (
                            <SpeakerHigh
                              size={32}
                              onClick={hanldeMute}
                              className="icon"
                            />
                          )}
                          <Slider
                            min={0}
                            max={100}
                            value={muted ? 0 : volume * 100}
                            onChange={handleVolumeChange}
                            className="slider"
                          />
                        </div>
                      </div>
                      <div className="right-controls">
                        {/* Hide playback rate for live videos */}
                        {!isLiveVideoPlayable && playbackRateContent()}
                        {pipSupport && (
                          <PictureInPicture size={32} onClick={pipHandler} />
                        )}
                        {!isLiveVideoPlayable && chromeCastSupport && (
                          <Screencast size={32} onClick={handleCasting} />
                        )}
                        {!isLiveVideoPlayable && airplaySupport && (
                          <Airplay size={32} onClick={handleAirplay} />
                        )}
                        {!isFullScreen ? (
                          <ArrowsOut size={32} onClick={toggleFullScreen} />
                        ) : (
                          <ArrowsIn size={32} onClick={toggleFullScreen} />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </IOSWrapper>
            {previewVideo && deviceWidth !== 'mobile' && (
              <PreviewIndicator
                duration={duration}
                played={played}
                setShowSignUpModal={setShowSignUpModal}
              />
            )}
          </>
        )}
      </div>
      {previewVideo && deviceWidth === 'mobile' && (
        <PreviewIndicator
          duration={duration}
          played={played}
          setShowSignUpModal={setShowSignUpModal}
        />
      )}
      {!isLiveVideoPlayable && (
        <div className="ml-20">
          <Chapters
            chapters={chapters}
            time={playedSeconds}
            handleChapter={handleChapter}
          />
        </div>
      )}
    </>
  );
}

export default CustomPlayer;
